import { yupResolver } from '@hookform/resolvers/yup'
import { ELanguage, getELoanPurposeList, getEProvinceList } from '@if/core/types'
import {
  ControlledPhoneField,
  ControlledPostalCodeField,
  ControlledSelect,
  ControlledTextField,
  useTranslatedOptionEnum,
} from '@if/mui'
import CloseIcon from '@mui/icons-material/Close'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem
} from '@mui/material'
import { AsyncActionButton } from '@src/components'
import { useCreateBanner } from '@src/data/api/merchants-api/merchants-api'
import { BannerDto, BannerSchema } from '@src/data/types/BannerSchema'
import { memo, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface Props {
  open: boolean
  title: string
  handleClose: () => void
}

const AddBannerDialog = ({ open, handleClose, title }: Props) => {
  const { t } = useTranslation()

  const { handleSubmit, reset, control } = useForm<BannerDto>({
    mode: 'onBlur',
    resolver: yupResolver(BannerSchema),
  })

  const [createBanner, isCreatingBanner, resetCreateBanner] = useCreateBanner()

  useEffect(() => {
    reset()
  }, [open, reset])

  const onSubmit = async (data: BannerDto) => {
    await createBanner(data).then((value) => {
      resetCreateBanner()
      if (value) handleClose()
    })
  }

  const loanPurposeOptions = useTranslatedOptionEnum('ELoanPurpose', getELoanPurposeList(), 'enum')
  const provinceOptions = useTranslatedOptionEnum('EProvince', getEProvinceList(), 'enum')

  return (
    <Dialog
      open={open}
      onClose={() => {
        handleClose()
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
      transitionDuration={0}
    >
      <DialogTitle style={{ borderBottom: '1px solid' }} id="alert-dialog-title" sx={{ textAlign: 'center' }}>
        <IconButton
          aria-label="close"
          onClick={() => {
            handleClose()
          }}
          style={{
            position: 'absolute',
            right: 15,
            top: 15,
          }}
        >
          <CloseIcon />
        </IconButton>
        {title}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }} alignItems="flex-start">
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ControlledTextField
              name="name"
              control={control}
              id="name"
              label={t('common.name')}
              slotProps={{
                htmlInput: { maxLength: 100 },
              }}
              fullWidth
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ControlledSelect
              name="serviceCategory"
              control={control}
              id="serviceCategory"
              label={t('common.serviceCategory')}
              options={loanPurposeOptions}
              fullWidth
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ControlledTextField
              name="email"
              control={control}
              type="email"
              label={t('common.email')}
              slotProps={{
                htmlInput: { maxLength: 256 },
              }}
              fullWidth
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ControlledPhoneField name="phone" control={control} label={t('common.phone')} fullWidth />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ControlledTextField
              id="website"
              control={control}
              name="website"
              label={t('common.website')}
              slotProps={{
                htmlInput: { maxLength: 256 },
              }}
              fullWidth
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ControlledSelect
              id="language"
              name="languageId"
              control={control}
              label={t('common.language')}
              defaultValue="en"
              fullWidth
            >
              <MenuItem value="0">{t(`ELanguage.${ELanguage.French}`, { ns: 'enum' })}</MenuItem>
              <MenuItem value="1">{t(`ELanguage.${ELanguage.English}`, { ns: 'enum' })}</MenuItem>
            </ControlledSelect>
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 12,
            }}
          >
            <ControlledTextField
              name="address"
              control={control}
              label={t('common.address')}
              slotProps={{
                htmlInput: { maxLength: 100 },
              }}
              fullWidth
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 4,
            }}
          >
            <ControlledTextField
              name="city"
              control={control}
              label={t('common.city')}
              slotProps={{
                htmlInput: { maxLength: 50 },
              }}
              fullWidth
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 4,
            }}
          >
            <ControlledSelect
              name="province"
              control={control}
              label={t('common.province')}
              options={provinceOptions}
              fullWidth
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 4,
            }}
          >
            <ControlledPostalCodeField
              name="zipCode"
              control={control}
              id="zipCode"
              label={t('common.postalCode')}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <AsyncActionButton
          color="primary"
          isPending={isCreatingBanner}
          onClick={async () => {
            await handleSubmit(onSubmit)()
          }}
        >
          {t('common.addBanner')}
        </AsyncActionButton>
      </DialogActions>
    </Dialog>
  )
}

export default memo(AddBannerDialog)
